import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { todayIso } from "@/features/trips/tripLabels";
import { CrossdockClient } from "@/features/crossdock/CrossdockClient";
import { crossdockKeys, getCrossdockDay, listCrossdockDepots, type CrossdockDepot, type CrossdockDirection } from "@/features/crossdock/crossdock.api";

// Warehouse → Crossdock (docs/tms-crossdock.md): what a depot unloads and loads on a day. The warehouse of the depot
// reads it, and the planning (CD4, Camiel 13-10-2026).

interface CrossdockSearch {
  depot?: string;
  date?: string;
  tab?: CrossdockDirection;
}

const READ = ["crossdock", "crossdock_write", "tms_planning", "tms_planning_write"] as const;

// The depot this person looked at last: a convenience of this browser, nothing breaks without it.
const DEPOT_KEY = "wlx:crossdock-depot";
function rememberedDepot(): string | null {
  try {
    return window.localStorage.getItem(DEPOT_KEY);
  } catch {
    return null;
  }
}
function rememberDepot(code: string) {
  try {
    window.localStorage.setItem(DEPOT_KEY, code);
  } catch {
    // Private window or blocked storage: the address still holds the choice.
  }
}

export const Route = createFileRoute("/_shell/warehouse/crossdock")({
  beforeLoad: ({ context }) => {
    if (!READ.some((key) => hasModule(context.session, key))) throw redirect({ to: firstAllowedRoute(context.session) });
  },
  validateSearch: (search: Record<string, unknown>): CrossdockSearch => ({
    depot: typeof search.depot === "string" && search.depot ? search.depot : undefined,
    date: typeof search.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.date) ? search.date : undefined,
    tab: search.tab === "outgoing" ? "outgoing" : search.tab === "incoming" ? "incoming" : undefined,
  }),
  component: CrossdockScreen,
});

function CrossdockScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  const depotsQuery = useQuery({ queryKey: crossdockKeys.depots, queryFn: listCrossdockDepots, staleTime: 60_000 });
  const depots = depotsQuery.data ?? [];
  const wanted = search.depot ?? rememberedDepot();
  const depot = depots.find((one) => one.code === wanted) ?? depots[0];
  const date = search.date ?? todayIso();
  const direction = search.tab ?? "incoming";

  const dayQuery = useQuery({
    queryKey: crossdockKeys.day(depot?.id ?? "", date),
    queryFn: () => getCrossdockDay(depot!.id, date),
    enabled: Boolean(depot),
    placeholderData: keepPreviousData,
    // The floor works from it: what arrives and leaves changes through the day.
    refetchInterval: 60_000,
  });

  function chooseDepot(next: CrossdockDepot) {
    rememberDepot(next.code);
    updateParams({ depot: next.code });
  }

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Warehouse Crossdock">
      {depotsQuery.error ? (
        <div className="wl-alert wl-alert--danger">
          <span>{depotsQuery.error instanceof Error ? depotsQuery.error.message : t("common.errorGeneric")}</span>
        </div>
      ) : !depot ? (
        <p className="t-body-sm t-muted">{depotsQuery.isLoading ? t("common.loading") : t("crossdock.noDepots")}</p>
      ) : (
        <CrossdockClient
          day={dayQuery.data?.depot.branchId === depot.id ? dayQuery.data : undefined}
          depots={depots}
          depot={depot}
          date={date}
          direction={direction}
          updateParams={updateParams}
          onChooseDepot={chooseDepot}
          isLoading={dayQuery.isLoading || (dayQuery.isFetching && dayQuery.data?.depot.branchId !== depot.id)}
          isFetching={dayQuery.isFetching}
          error={dayQuery.error}
          userId={session.userId}
          canWrite={hasModule(session, "crossdock_write") || hasModule(session, "tms_planning_write")}
        />
      )}
    </div>
  );
}
